import { ButtonBase, styled } from '@mui/material';
import { useState } from 'react';

import { CompetitionWinnersDialog } from '@/components/CompetitionWinnersDialog/index.ts';
import { UnderlinedText } from '@/components/UnderlinedText/index.ts';
import type { CompetitionWinner } from '@/lib/api/requests.ts';

import { CTA } from '../_components/CTA.tsx';
import { TopSectionBackground } from '../_components/TopSectionBackground.tsx';
import { useHasEnded } from '../_components/useHasEnded.ts';

const Circle = styled('span')(() => ({
  borderRadius: '9999999px',
  aspectRatio: '1 / 1',
  backgroundColor: 'white',
  display: 'inline-grid',
  height: '1.5em',
  lineHeight: 1,
  placeContent: 'center',
}));

const List = styled('ol')(({ theme }) => ({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(3),

  '& li': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },

  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(4.5),
  },
}));

const TextContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: 'auto',
  marginBottom: 'auto',
  display: 'flex',
  flexDirection: 'column',

  [theme.breakpoints.up('sm')]: {
    textAlign: 'center',
    alignItems: 'center',
  },
}));

const Card = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.text.default,
  margin: theme.spacing(0, 'auto'),
  padding: theme.spacing(2, 3),
  marginBottom: theme.spacing(3),

  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3, 4),
  },
}));

export const TopSectionJuly = (props: { winners: CompetitionWinner[] | undefined }) => {
  const { winners } = props;
  const [open, setOpen] = useState(false);
  const ended = useHasEnded();

  return (
    <TopSectionBackground sunPosition="low">
      <TextContainer>
        <Card>
          <div className="text-center typography-v1-h3">
            {ended ? (
              <>Vi trakk 5 nye vinnere!</>
            ) : (
              <>
                Vi trakk 10 vinnere i juni.
                <br />
                Nå skal vi <UnderlinedText color="white">trekke 5 til!</UnderlinedText>
              </>
            )}
          </div>
        </Card>

        <h1 className="mb-8 typography-v1-display">
          Send gjelden
          <br />
          din på ferie
        </h1>

        <div className="typography-v1-body4">
          <List>
            <li>
              <Circle>1</Circle>
              Sjekk din gjeld
            </li>
            <li>
              <Circle>2</Circle>
              Se hvor mye du betaler i løpet av et år
            </li>
            <li>
              <Circle>3</Circle>
              Delta i trekningen
            </li>
          </List>
        </div>

        <div className="mb-6 max-w-[50ch] typography-v1-h4 font-normal text-balance md:mb-8">
          {ended ? (
            <>
              Denne trekningen er avsluttet.
              <br />
              Vi hadde så stor interesse rundt å sende gjelden på ferie, at vi bestemte oss for å
              utvide kampanjen. Derfor trakk vi{' '}
              <ButtonBase onClick={() => setOpen(true)}>
                <u>
                  <b>5 nye vinnere</b>
                </u>
              </ButtonBase>
            </>
          ) : (
            <>Skulle du ønske at vi overtok gjelden din et helt år?</>
          )}
        </div>

        <CTA />
      </TextContainer>
      {winners && (
        <CompetitionWinnersDialog winners={winners} open={open} onClose={() => setOpen(false)} />
      )}
    </TopSectionBackground>
  );
};
